import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "./../Footer";
import Header from "./../Header";
import useSeo from "../../hooks/useSeo";
import { SEO_PAGES } from "../../constants/seo";
import {
  CONSULTANT_SKILL_OPTIONS,
  fetchConsultants,
  getConsultantImageUrl,
} from "../../utils/consultants";

export default function ConsultantList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  useSeo(SEO_PAGES.consultants);

  const [consultants, setConsultants] = useState([]);
  const [keyword, setKeyword] = useState(() => searchParams.get("keyword") || "");
  const [appliedKeyword, setAppliedKeyword] = useState(
    () => searchParams.get("keyword") || ""
  );
  const [skill, setSkill] = useState(() => searchParams.get("skill") || "");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const presetKeyword = searchParams.get("keyword") || "";
    const presetSkill = searchParams.get("skill") || "";
    setKeyword(presetKeyword);
    setAppliedKeyword(presetKeyword);
    setSkill(presetSkill);
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchConsultants({
          keyword: appliedKeyword,
          skill,
        });
        if (!cancelled) setConsultants(data);
      } catch (err) {
        console.error(err);
        if (!cancelled) setConsultants([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [appliedKeyword, skill]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setAppliedKeyword(keyword.trim());
  };

  const handleConsult = (consultant) => {
    if (!consultant.is_online) {
      alert("该咨询师当前暂停接单");
      return;
    }
    navigate(`/consultants2/${consultant.id}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">咨询师列表</h1>
          <p className="text-gray-500 text-sm mb-4">
            认证咨询师优先 · 在线接单优先 · 资深咨询师优先
          </p>

          <form
            onSubmit={handleSearch}
            className="flex flex-wrap gap-3 items-center"
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索咨询师、城市、擅长方向"
              className="flex-1 min-w-[220px] border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-blue-300"
            />
            <select
              value={skill}
              onChange={(e) => setSkill(e.target.value)}
              className="border rounded-xl px-4 py-3 bg-white outline-none focus:ring-2 focus:ring-blue-300"
            >
              {CONSULTANT_SKILL_OPTIONS.map((item) => (
                <option key={item.value || "all"} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            <button
              type="submit"
              className="px-6 py-3 rounded-xl bg-blue-500 text-white font-medium hover:bg-blue-600"
            >
              搜索
            </button>
          </form>
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl shadow p-10 text-center text-gray-400">
            加载中...
          </div>
        ) : consultants.length === 0 ? (
          <div className="bg-white rounded-2xl shadow p-10 text-center text-gray-400">
            暂无匹配的咨询师
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {consultants.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow p-6 flex flex-col"
              >
                <div className="flex items-center gap-4 mb-5">
                  <button
                    type="button"
                    onClick={() => navigate(`/consultants2/${item.id}`)}
                    className="flex items-center gap-4 text-left hover:opacity-90 flex-1 min-w-0"
                  >
                  {getConsultantImageUrl(item.avatar) ? (
                    <img
                      src={getConsultantImageUrl(item.avatar)}
                      alt={item.name}
                      className="w-20 h-20 rounded-full object-cover bg-gray-100 shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-2xl font-bold shrink-0">
                      {(item.name || "?").slice(0, 1)}
                    </div>
                  )}

                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-bold text-gray-800 hover:text-blue-600">
                        {item.name}
                      </h2>
                      {item.is_verified ? (
                        <span className="text-xs bg-blue-100 text-blue-600 px-2 py-1 rounded-full">
                          已认证
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">
                          未认证
                        </span>
                      )}
                      {item.experience_years >= 5 ? (
                        <span className="text-xs bg-amber-50 text-amber-600 px-2 py-1 rounded-full">
                          资深
                        </span>
                      ) : null}
                    </div>

                    <p className="text-gray-500 text-sm mt-1">
                      {item.city || "未填写城市"}
                      {item.experience_years
                        ? ` · ${item.experience_years}年经验`
                        : ""}
                    </p>

                    <p
                      className={
                        item.is_online
                          ? "text-green-500 text-sm mt-1"
                          : "text-gray-400 text-sm mt-1"
                      }
                    >
                      {item.is_online ? "在线接单" : "暂停接单"}
                    </p>
                  </div>
                  </button>
                </div>

                <div className="space-y-2 text-sm text-gray-600 flex-1">
                  <p>
                    <span className="text-gray-400">擅长方向：</span>
                    {item.specialties || "暂未填写"}
                  </p>
                  <p>
                    <span className="text-gray-400">咨询风格：</span>
                    {item.consultation_style || "暂未填写"}
                  </p>
                  <p className="line-clamp-3">
                    <span className="text-gray-400">简介：</span>
                    {item.intro || "这位咨询师还没有填写简介"}
                  </p>
                </div>

                <div className="border-t mt-5 pt-5 flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-gray-400 text-sm">单次咨询</p>
                    <p className="text-xl font-bold text-gray-800">
                      ¥{item.price || 0}
                      <span className="text-sm text-gray-400 ml-1">
                        / {item.duration || 50}分钟
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleConsult(item)}
                      disabled={item.is_online === false}
                      className={`flex-1 px-4 py-2 rounded-xl text-white font-bold ${
                        item.is_online !== false
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-gray-300 cursor-not-allowed"
                      }`}
                    >
                      立即咨询
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
